import { ApiResponse } from "../../interfaces/genericResponse/GenericResponse";
import { GetCurrentSupervisionDataRequest, WeeklySupervisionResponse } from "../../interfaces/weeklysupervison/Weeklysupervision";


export type SupervisionStatus = "COMPLETED" | "IN PROGRESS" | "INCOMPLETE/OVERDUE" | "INCOMPLETE/MISSING";

export interface SupervisionWeek {
    weekOf: string;
    status: SupervisionStatus;
    instanceId?: number | null;
}

export interface SupervisionCadenceApiResponse {
    firstName: string;
    lastName: string;
    scId: string;
    id: string;
    flagCount: number;
    history: SupervisionWeek[];
}

export interface SupervisionCadenceDto extends SupervisionCadenceApiResponse {
    firstName: string;
    lastName: string;
    scId: string;
    id: string;
    flagCount: number;
}

export async function getSupervisorCadence(idToken: string, supervisorId: string): Promise<SupervisionCadenceApiResponse[]> {
    const apiUrl = `${process.env.REACT_APP_API_BASE_URL}/reporting/weekly-supervision/supervisor/${supervisorId}/cadence`;

    const response = await fetch(apiUrl, {
        method: 'GET',
        headers: {
            'Authorization': `Bearer ${idToken}`,
            'Content-Type': 'application/json',
        },
    });

    if (!response.ok) {
        const errorBody = await response.json().catch(() => ({}));
        throw {
            status: response.status,
            message: 'Failed to fetch supervision cadence data',
            details: errorBody,
        };
    }

    const result = await response.json();
    console.log('Canence Response', result);
    return result.data;
}

export const weeklySupervisionService = async (
    idToken: string,
    pageNumber: number,
    pageSize: number,
    subTask?: string,
    participantName?: string // <--- 1. Add this argument
): Promise<any> => {
    const apiUrl: string = `${process.env.REACT_APP_API_BASE_URL}/reporting/weekly-supervision/current`;

    // 2. Add participantName to the payload
    const payload: GetCurrentSupervisionDataRequest = {
        taskSubTypeName: subTask || null,
        pageNumber: pageNumber,
        pageSize: pageSize,
        participantName: participantName || null // <--- Map it here
    }

    console.log('Payload', payload)
    try {
        const response = await fetch(apiUrl, {
            method: 'POST',
            headers: {
                'Authorization': `Bearer ${idToken}`,
                'Content-Type': 'application/json',
            },
            body: JSON.stringify(payload)
        });

        if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status}`);
        }

        const data = await response.json();
        console.log("Weekly Supervision Response:", data);
        return data;

    } catch (err) {
        console.error("Error fetching weekly supervision data:", err);
        throw err;
    }
};


// This interface defines the structure for the request body
interface CommentRequestBody {
    taskId: number | string; // Use number or string depending on your data model
    commentText: string;
}

// This interface defines the parameters for our service function
interface PostCommentParams {
    idToken: string;
    taskId: number | string;
    commentText: string;
    instanceId: string | undefined;
}

export interface TaskProgressNoteDto {
    id: number;
    taskId: number;
    noteText: string;
    lastUpdatedBy: {
        id: string;
        firstName: string;
        lastName: string;
        displayName: string;
        email?: string;
    };
    lastUpdatedAt: string;
}

export const getTaskProgressNote = async (
    idToken: string,
    taskId: string | number
): Promise<TaskProgressNoteDto | null> => {
    const apiUrl = `${process.env.REACT_APP_API_BASE_URL}/reporting/weekly-supervision/task/${taskId}/progress-note`;

    try {
        const response = await fetch(apiUrl, {
            method: 'GET',
            headers: {
                'Authorization': `Bearer ${idToken}`,
                'Content-Type': 'application/json',
            },
        });

        if (response.status === 404) {
            // According to docs, 404 means "Task progress note not found."
            // We return null so the UI knows there is no note yet, but it's not a system error.
            return null;
        }

        if (!response.ok) {
            const errorBody = await response.json().catch(() => ({}));
            throw {
                status: response.status,
                message: errorBody.message || 'Failed to fetch task progress note',
                details: errorBody,
            };
        }

        const result = await response.json();
        return result.data; // Returning the data object containing noteText, lastUpdatedBy, etc.

    } catch (err) {
        console.error("Error fetching task progress note:", err);
        throw err;
    }
};

export const postSupervisionComment = async (params: PostCommentParams): Promise<any> => {
    const { idToken, taskId, commentText, instanceId } = params;

    // Construct the URL with query parameters
    const apiUrl = `${process.env.REACT_APP_API_BASE_URL}/reporting/weekly-supervision/${instanceId}/comments`;

    // Create the request body payload as an array, as per your example
    const requestBody: CommentRequestBody[] = [
        {
            taskId: taskId,
            commentText: commentText,
        },
    ];

    try {
        const response = await fetch(apiUrl, {
            method: 'POST', // Use POST to create a new resource
            headers: {
                'Authorization': `Bearer ${idToken}`,
                'Content-Type': 'application/json',
            },
            body: JSON.stringify(requestBody), // Convert the request body to a JSON string
        });

        if (!response.ok) {
            // If response is not 200-299, get more details and throw an error
            const errorData = await response.text(); // or response.json() if the error response is JSON
            throw {
                status: response.status,
                message: `HTTP error! status: ${response.status}, details: ${errorData}`
            };
        }

        // Assuming the API returns the newly created comment object
        const savedComment = await response.json();

        console.log("Comment saved successfully:", savedComment);
        return savedComment;

    } catch (err) {
        console.error("Error posting supervision comment:", err);
        throw err;
    }
};


// New function to get the current, live supervision data for an employee
export async function getCurrentWeeklySupervisionByEmployee(
    idToken: string,
    employeeId: string,
    taskSubTypeName: string,
    pageNumber: number = 1,
    pageSize: number = 1000,
    participantName?: string // <--- Added parameter
): Promise<WeeklySupervisionResponse> {
    const url = new URL(`${process.env.REACT_APP_API_BASE_URL}/reporting/weekly-supervision/current/employee/${employeeId}`);
    url.searchParams.append('pageNumber', pageNumber.toString());
    url.searchParams.append('pageSize', pageSize.toString());

    if (taskSubTypeName) {
        url.searchParams.append('taskSubTypeName', taskSubTypeName);
    }
    if (participantName) { // <--- Added param handling
        url.searchParams.append('participantName', participantName);
    }

    const response = await fetch(url.toString(), {
        method: 'GET',
        headers: {
            'Authorization': `Bearer ${idToken}`,
            'Content-Type': 'application/json',
        },
    });

    if (!response.ok) {
        const errorBody = await response.json().catch(() => ({}));
        throw {
            status: response.status,
            message: 'Failed to fetch current weekly supervision data',
            details: errorBody,
        };
    }

    const result: ApiResponse<WeeklySupervisionResponse> = await response.json();
    if (!result.data || !Array.isArray(result.data.tasks)) {
        throw new Error("API response is malformed for current supervision data.");
    }
    return result.data;
}

// Updated function to get historical supervision data
export async function getHistoricalWeeklySupervisionByEmployee(
    idToken: string,
    instanceId: number,
    employeeId: string,
    taskSubTypeName: string,
    pageNumber: number = 1,
    pageSize: number = 1000,
    participantName?: string // <--- Added parameter
): Promise<WeeklySupervisionResponse> {
    const url = new URL(`${process.env.REACT_APP_API_BASE_URL}/reporting/weekly-supervision/instance/${instanceId}/employee/${employeeId}`);
    url.searchParams.append('pageNumber', pageNumber.toString());
    url.searchParams.append('pageSize', pageSize.toString());

    if (taskSubTypeName) {
        url.searchParams.append('taskSubTypeName', taskSubTypeName);
    }
    if (participantName) { // <--- Added param handling
        url.searchParams.append('participantName', participantName);
    }

    const response = await fetch(url.toString(), {
        method: 'GET',
        headers: {
            'Authorization': `Bearer ${idToken}`,
            'Content-Type': 'application/json',
        },
    });

    if (!response.ok) {
        const errorBody = await response.json().catch(() => ({}));
        throw {
            status: response.status,
            message: 'Failed to fetch historical weekly supervision data',
            details: errorBody,
        };
    }

    const result: ApiResponse<WeeklySupervisionResponse> = await response.json();
    if (!result.data || !Array.isArray(result.data.tasks)) {
        throw new Error("API response is malformed for historical supervision data.");
    }
    return result.data;
}

export async function getSupportCoordinators(idToken: string): Promise<any> {
    // This function should call your backend API to get a list of users
    // who have the "Support Coordinator" role.
    // Example placeholder:
    const response = await fetch('/api/support-coordinators', {
        headers: {
            'Authorization': `Bearer ${idToken}`,
            'Content-Type': 'application/json',
        },
    });
    if (!response.ok) {
        throw new Error('Failed to fetch support coordinators');
    }
    const data = await response.json();
    return data; // Assuming data structure: { data: { supportCoordinators: [{id: '...', name: '...'}], ... } }
}

export async function getSupervisorWeeklySupervision(idToken: string, coordinatorId: string): Promise<any> {
    // This function should call your backend API to get weekly supervision tasks
    // filtered by the provided coordinatorId.
    // Example placeholder:
    const response = await fetch(`/api/supervisor-weekly-supervision?coordinatorId=${coordinatorId}`, {
        headers: {
            'Authorization': `Bearer ${idToken}`,
            'Content-Type': 'application/json',
        },
    });
    if (!response.ok) {
        throw new Error('Failed to fetch supervisor weekly supervision data');
    }
    const data = await response.json();
    return data; // Assuming data structure: { data: { tasks: [...], supervisionInstance: {...} } }
}

export async function getSupervisorSC(idToken: string, supervisorId: string): Promise<any[]> {
    const apiUrl = `${process.env.REACT_APP_API_BASE_URL}/reporting/weekly-supervision/by-supervisor/${supervisorId}`;

    const response = await fetch(apiUrl, {
        method: 'GET',
        headers: {
            'Authorization': `Bearer ${idToken}`,
            'Content-Type': 'application/json',
        },
    });

    if (!response.ok) {
        const errorBody = await response.json().catch(() => ({}));
        throw {
            status: response.status,
            message: 'Failed to fetch supervisor weekly supervision data',
            details: errorBody,
        };
    }

    const result = await response.json();

    if (!Array.isArray(result.data)) {
        throw new Error("Expected 'data' to be an array");
    }

    return result.data; // ✅ return the actual array
}

export const completeAndRolloverSupervision = async (
    idToken: string,
    instanceId: number | undefined
): Promise<Blob> => {
    // The supervisorId is removed from the query string, assuming the backend will use the authenticated user's ID
    const apiUrl = `${process.env.REACT_APP_API_BASE_URL}/reporting/weekly-supervision/${instanceId}/complete-and-rollover`;

    try {
        const response = await fetch(apiUrl, {
            method: 'POST',
            headers: {
                'Authorization': `Bearer ${idToken}`,
            },
        });

        if (!response.ok) {
            const errorBody = await response.json().catch(() => ({ message: "An unknown error occurred." }));
            throw new Error(errorBody.message || `HTTP error! status: ${response.status}`);
        }

        const blob = await response.blob();

        // Trigger download
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `Weekly-Supervision-Report-${instanceId}.pdf`;
        document.body.appendChild(a);
        a.click();
        a.remove();
        window.URL.revokeObjectURL(url);

        return blob;

    } catch (err) {
        console.error("Error completing supervision report:", err);
        throw err;
    }
};

export async function getBarGraphData(idToken: string, userId?: string | null): Promise<any[]> {
    // Construct URL based on whether userId is present
    let apiUrl = `${process.env.REACT_APP_API_BASE_URL}/reporting/weekly-supervision/bar-graph-data`;

    // Only append param if userId exists
    if (userId) {
        apiUrl += `?userId=${userId}`;
    }

    const response = await fetch(apiUrl, {
        method: 'GET',
        headers: {
            'Authorization': `Bearer ${idToken}`,
            'Content-Type': 'application/json',
        },
    });

    if (!response.ok) {
        const errorBody = await response.json().catch(() => ({}));
        throw {
            status: response.status,
            message: 'Failed to fetch bar graph data',
            details: errorBody,
        };
    }

    const result = await response.json();

    if (!Array.isArray(result)) {
        throw new Error("Expected response to be an array");
    }

    return result;
}